import { DesignToken } from "@livechat/design-system-react-components";

import { StatusTag } from "components/status-tag/StatusTag";
import { ReportDistribution } from "constants/reports/distribution";
import { duration } from "helpers/date";
import { TicketStatus } from "interfaces/entities/ticket";
import { IHandlingTimeByStatus, IHandlingTimeByStatusPerEntity } from "services/api/report/interfaces";
import { withVar } from "themes/helpers";

import { getLabelsFromPeriod, getSeriesDataFromPeriod } from "../helpers";
import { IChartData } from "../interfaces";

type HandlingTimeByStatusData = {
  data: {
    handlingTimeByStatus: IHandlingTimeByStatus;
    handlingTimeByStatusTotal: IHandlingTimeByStatus;
    handlingTimeByStatusPerTeam: Record<string, IHandlingTimeByStatus>;
    handlingTimeByStatusPerAgent: Record<string, IHandlingTimeByStatus>;
  };
};

function mapToArray(data: Record<string, IHandlingTimeByStatus>): IHandlingTimeByStatusPerEntity[] {
  return Object.entries(data)
    .map(([id, values]) => ({
      id,
      ...values,
      total: values.open + values.pending + values.onhold,
    }))
    .sort((a, b) => a.total - b.total);
}

export function handlingTimeByStatusDeserializer({
  data,
}: HandlingTimeByStatusData): Record<string, IChartData | IHandlingTimeByStatusPerEntity[]> {
  const totalMedian = {
    id: "total",
    open: data.handlingTimeByStatusTotal.open,
    pending: data.handlingTimeByStatusTotal.pending,
    onhold: data.handlingTimeByStatusTotal.onhold,
    total:
      data.handlingTimeByStatusTotal.open +
      data.handlingTimeByStatusTotal.pending +
      data.handlingTimeByStatusTotal.onhold,
    count: 0,
  };
  return {
    handlingTimeByStatus: {
      labels: getLabelsFromPeriod(data.handlingTimeByStatus, ReportDistribution.Day),
      series: [
        {
          name: "Open",
          data: getSeriesDataFromPeriod(data.handlingTimeByStatus, "open", ReportDistribution.Day),
          color: withVar(DesignToken.SurfaceAccentEmphasisHighInfo),
          extras: {
            breakdownCustomElement: (
              <div>
                <StatusTag status={TicketStatus.Open} />
              </div>
            ),
          },
        },
        {
          name: "Pending",
          data: getSeriesDataFromPeriod(data.handlingTimeByStatus, "pending", ReportDistribution.Day),
          color: withVar(DesignToken.DecorGray400),
          extras: {
            breakdownCustomElement: (
              <div>
                <StatusTag status={TicketStatus.Pending} />
              </div>
            ),
          },
        },
        {
          name: "On hold",
          data: getSeriesDataFromPeriod(data.handlingTimeByStatus, "onhold", ReportDistribution.Day),
          color: withVar(DesignToken.DecorPurple500),
          extras: {
            breakdownCustomElement: (
              <div>
                <StatusTag status={TicketStatus.OnHold} />
              </div>
            ),
          },
        },
      ],
      summary: [
        {
          name: "Open status median",
          value: duration(data.handlingTimeByStatusTotal.open),
          color: withVar(DesignToken.SurfaceAccentEmphasisHighInfo),
        },
        {
          name: "Pending status median",
          value: duration(data.handlingTimeByStatusTotal.pending),
          color: withVar(DesignToken.DecorGray400),
        },
        {
          name: "On hold status median",
          value: duration(data.handlingTimeByStatusTotal.onhold),
          color: withVar(DesignToken.DecorPurple500),
        },
      ],
    },
    handlingTimeByPerTeam: mapToArray({ totalMedian, ...data.handlingTimeByStatusPerTeam }),
    handlingTimeByPerAgent: mapToArray({ totalMedian, ...data.handlingTimeByStatusPerAgent }),
  };
}
